import { clsx } from 'clsx';
import type { ReactNode } from 'react';

/**
 * Canonical card wrapper. Mirrors JPP's bo-dashboard card style:
 *   - rounded-[20px] (via --radius-card)
 *   - white background
 *   - subtle shadow (var(--shadow-card))
 *   - mobile-first padding
 *
 * Use <Card> as the visual container; compose with <Card.Header> / <Card.Body>
 * if you want vertical structure, or pass children directly for free-form layout.
 */
export function Card({
  children,
  className,
  minH = '0',
}: {
  children: ReactNode;
  className?: string;
  /** Optional min-height — JPP uses 474px on dashboard cards for grid alignment. */
  minH?: string;
}) {
  return (
    <div
      className={clsx(
        'relative flex size-full flex-col rounded-[var(--radius-card)] bg-white shadow-[var(--shadow-card)]',
        'px-5 py-6 sm:p-6',
        className,
      )}
      style={minH !== '0' ? { minHeight: minH } : undefined}
    >
      {children}
    </div>
  );
}

Card.Header = function CardHeader({ title, action }: { title: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-4 flex items-center justify-between">
      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      {action}
    </div>
  );
};

Card.Body = function CardBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={clsx('flex-1', className)}>{children}</div>;
};
